/* eslint-disable react/display-name */
import React from 'react';
import {
    Chart as ChartJS,
    LinearScale,
    CategoryScale,
    TimeScale,
    BarElement,
    PointElement,
    LineElement,
    Legend,
    Tooltip,
    Title,
    SubTitle,
    defaults,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { LoadingDots } from '@app/ui';
import addCanvasBackground from './addCanvasBackground';

defaults.font.family = 'aspiralight, pnlight';
ChartJS.register(
    LinearScale,
    CategoryScale,
    TimeScale,
    BarElement,
    PointElement,
    LineElement,
    Legend,
    Tooltip,
    Title,
    SubTitle,
    addCanvasBackground,
);

import * as R from 'ramda';

// Graph Config section
import baseConfig from '../../utils/charts/base-config';
import mergeConfigs from '../../utils/charts/merge-configs';

import currencyY from '../../utils/charts/currency-y-axis-config';
import secY from '../../utils/charts/sec-y-axis-config';
import dateY from '../../utils/charts/date-y-axis-config';
import decimalY from '../../utils/charts/decimal-y-axis-config';
import percentageY from '../../utils/charts/percentage-y-axis-config';

import categoryX from '../../utils/charts/category-x-axis-config';
import currencyX from '../../utils/charts/currency-x-axis-config';
import dateX from '../../utils/charts/date-x-axis-config';

const yAxisConfigs = {
    currency: currencyY,
    percentage: percentageY,
    decimal: decimalY,
    date: dateY,
    sec: secY,
    // category: categoryY,
};

const xAxisConfigs = {
    currency: currencyX,
    date: dateX,
    category: categoryX,
    // percentage: percentageX,
    // decimal: decimalX,
};

const strategy = (key, left, right) => {
    if (key === 'xAxes' || key === 'yAxes') {
        return [R.mergeDeepRight(left[0], right[0])];
    }
    return right;
};

const lineDefault = color => ({
    fill: false,
    lineTension: 0,
    backgroundColor: color,
    borderColor: color,
    borderCapStyle: 'butt',
    borderWidth: 1.5,
    borderDash: [],
    borderDashOffset: 0.0,
    borderJoinStyle: 'miter',
    pointBorderColor: '#fff',
    pointBackgroundColor: color,
    pointBorderWidth: 1,
    pointHoverRadius: 2,
    pointHoverBackgroundColor: color,
    pointHoverBorderColor: color,
    pointHoverBorderWidth: 2,
    pointRadius: 3,
    pointHitRadius: 10,
});

const mapIndexed = R.addIndex(R.map);
const appendColor = mapIndexed(set => R.merge(lineDefault(set.color), set));

export type LineChartOptionsProp = {
    config?: any;
    currency?: string;
    title?: string;
    isDoubled?: boolean;
    isStacked?: boolean;
    xType:
        | 'currency'
        | 'percentage'
        | 'decimal'
        | 'date'
        | 'category'
        | 'categoryStacked';
    yType:
        | 'currency'
        | 'percentage'
        | 'decimal'
        | 'doubleDecimal'
        | 'date'
        | 'category'
        | 'sec'
        | 'decimalStacked';
    plugins?: any;
};

export type LineChartProps = {
    data: {
        datasets: { data: string[] | number[] }[];
        labels: string[][] | string[] | number[];
    };
    isLoading: boolean;
    options?: LineChartOptionsProp;
};

const LineChart = React.forwardRef(
    ({ data, isLoading, options }: LineChartProps, ref: any) => {
        // eslint-disable-next-line no-unused-vars
        const { currency = 'USD', xType, yType, title, config = {} } = options;
        const { labels } = data;

        const chartConfig = mergeConfigs(
            strategy,
            baseConfig,
            xAxisConfigs[xType]({ currency, labels }),
            yAxisConfigs[yType]({ currency, labels }),
            title ? { title: { text: title } } : {},
            config,
        );

        const parsedData = R.evolve({ datasets: appendColor }, data); // CHANGE TO REDUCE TO ACCEPT MULTIPLE DATASETS

        return isLoading ? (
            <LoadingDots size="lg" />
        ) : (
            <Line ref={ref} data={parsedData} options={chartConfig} redraw />
        );
    },
);

export default LineChart;
